import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mail, MessageCircle, HelpCircle, Book } from 'lucide-react';

export default function Support() {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 flex flex-col">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                  alt="FaheemGPT Logo" 
                  className="w-10 h-10 rounded-lg object-cover" 
                />
            </Link>
            <span className="text-xl font-bold tracking-tight">FaheemGPT <span className="text-slate-400 font-normal">Intelligent Tutoring Platform</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/">
                <Button variant="ghost" className="flex gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-6 bg-slate-50 border-b border-slate-100 text-center">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">How can we help you?</h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Find answers to common questions or get in touch with our support team directly.
        </p>
      </section>

      {/* Support Options */}
      <section className="py-16 px-6 flex-1">
        <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                
                <Card className="hover:shadow-md transition-shadow border-slate-200">
                    <CardHeader>
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 text-blue-600">
                            <Book className="w-6 h-6" />
                        </div>
                        <CardTitle>Knowledge Base</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600 mb-4">
                            Browse tutorials, guides, and FAQs to learn how to make the most of FaheemGPT.
                        </p>
                        <Button variant="outline" className="w-full border-slate-300 cursor-not-allowed bg-slate-50 text-slate-400" disabled>Coming Soon</Button>
                    </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow border-slate-200">
                    <CardHeader>
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 text-green-600">
                            <MessageCircle className="w-6 h-6" />
                        </div>
                        <CardTitle>Community Forum</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600 mb-4">
                            Connect with other teachers and students. Share tips, ask questions, and solve problems together.
                        </p>
                        <Button variant="outline" className="w-full border-slate-300 cursor-not-allowed bg-slate-50 text-slate-400" disabled>Coming Soon</Button>
                    </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow border-slate-200">
                    <CardHeader>
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 text-purple-600">
                            <Mail className="w-6 h-6" />
                        </div>
                        <CardTitle>Contact Support</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-slate-600 mb-4">
                            Need direct assistance? Our team is here to help with technical issues or account inquiries.
                        </p>
                        <a href="mailto:support@faheemgpt.com">
                            <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">Email Us</Button>
                        </a>
                    </CardContent>
                </Card>

            </div>

            {/* FAQ Preview */}
            <div className="mt-20 max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
                <div className="space-y-6">
                    <div className="p-6 border border-slate-200 rounded-xl bg-white">
                        <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <HelpCircle className="w-4 h-4 text-blue-500" />
                            Is FaheemGPT free for students?
                        </h3>
                        <p className="text-slate-600">
                            We offer both free and premium plans. The basic "Introduction to CS" course is free for all registered students.
                        </p>
                    </div>
                    <div className="p-6 border border-slate-200 rounded-xl bg-white">
                        <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <HelpCircle className="w-4 h-4 text-blue-500" />
                            How do I reset my password?
                        </h3>
                        <p className="text-slate-600">
                            Go to the login page and click "Forgot Password". Follow the email instructions to reset your credentials.
                        </p>
                    </div>
                    <div className="p-6 border border-slate-200 rounded-xl bg-white">
                        <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                            <HelpCircle className="w-4 h-4 text-blue-500" />
                            Can I use FaheemGPT on mobile?
                        </h3>
                        <p className="text-slate-600">
                            Yes! Our platform is fully responsive and works on smartphones and tablets directly through your web browser.
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-8 h-8 rounded-lg object-cover" 
            />
             <span className="font-semibold text-slate-700">FaheemGPT Intelligent Tutoring Platform</span>
          </div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/support" className="hover:text-slate-900 font-medium text-slate-900">Support</Link>
          </div>
          <div>
            &copy; 2024 FaheemGPT Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
